import { useEffect, useState } from 'react';
import { getUnitFaults, type UnitFaultSummary } from '@/lib/unitFaultsApi';

interface UseUnitFaultsResult {
  data: UnitFaultSummary | null;
  isLoading: boolean;
  error: string | null;
}

interface Loaded {
  unitId: string;
  data: UnitFaultSummary | null;
  error: string | null;
}

/**
 * One trailer's faults and warnings. Does nothing unless `enabled` (staff): a customer's page
 * never asks. A 403 reads as "nothing to show", the same as no data.
 *
 * `refreshKey` asks again when it changes; the page passes its live fault and warning counts, so
 * the rows keep up with the banners. Each result remembers the trailer it was read for, so a
 * trailer never shows the previous trailer's codes while its own load.
 */
export function useUnitFaults(
  unitId: string | null | undefined,
  enabled: boolean,
  refreshKey?: string | number,
): UseUnitFaultsResult {
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    if (!enabled || !unitId) return undefined;
    let cancelled = false;
    getUnitFaults(unitId)
      .then((summary) => { if (!cancelled) setLoaded({ unitId, data: summary, error: null }); })
      .catch((err: unknown) => {
        if (!cancelled) setLoaded({ unitId, data: null, error: err instanceof Error ? err.message : 'Failed to load' });
      });
    return () => { cancelled = true; };
  }, [unitId, enabled, refreshKey]);

  const current = enabled && unitId && loaded?.unitId === unitId ? loaded : null;
  return {
    data: current?.data ?? null,
    error: current?.error ?? null,
    isLoading: Boolean(enabled && unitId && !current),
  };
}
